import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const CreateClubModal = ({ onClose, onCreated }) => {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    club_type: 'group',
    is_private: false,
    // Extended fields for clubs
    about: '',
    location_address: '',
    contact_email: '',
    contact_phone: '',
    website: ''
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, club_type: type }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Only include extended fields if it's a club
      const payload = {
        name: formData.name,
        description: formData.description,
        club_type: formData.club_type,
        is_private: formData.is_private
      };

      if (formData.club_type === 'club') {
        if (formData.about) payload.about = formData.about;
        if (formData.location_address) payload.location_address = formData.location_address;
        if (formData.contact_email) payload.contact_email = formData.contact_email;
        if (formData.contact_phone) payload.contact_phone = formData.contact_phone;
        if (formData.website) payload.website = formData.website;
      }

      const res = await authFetch('/api/clubs', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        onCreated();
      } else {
        const data = await res.json();
        setError(data.detail || `Failed to create ${formData.club_type}`);
      }
    } catch (err) {
      setError(`Failed to create ${formData.club_type}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-club-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create {formData.club_type === 'club' ? 'Club' : 'Group'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="modal-error">{error}</div>}

          {/* Type Selector */}
          <div className="type-selector">
            <button
              type="button"
              className={`type-btn ${formData.club_type === 'group' ? 'active' : ''}`}
              onClick={() => handleTypeSelect('group')}
            >
              <span className="type-icon">👥</span>
              <span className="type-label">Group</span>
              <span className="type-desc">Simple friend group</span>
            </button>
            <button
              type="button"
              className={`type-btn ${formData.club_type === 'club' ? 'active' : ''}`}
              onClick={() => handleTypeSelect('club')}
            >
              <span className="type-icon">🏃</span>
              <span className="type-label">Club</span>
              <span className="type-desc">Formal organization</span>
            </button>
          </div>

          <div className="form-group">
            <label>{formData.club_type === 'club' ? 'Club' : 'Group'} Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={formData.club_type === 'club' ? 'e.g., City Running Club' : 'e.g., Weekend Runners'}
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={`Describe your ${formData.club_type}...`}
              rows={3}
            />
          </div>

          {/* Extended fields for clubs */}
          {formData.club_type === 'club' && (
            <div className="club-extended-fields">
              <div className="form-group">
                <label>About / Mission</label>
                <textarea
                  name="about"
                  value={formData.about}
                  onChange={handleChange}
                  placeholder="Share your club's mission, history, and values..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  name="location_address"
                  value={formData.location_address}
                  onChange={handleChange}
                  placeholder="e.g., Central Park, New York"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="club@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="+1 555 123 4567"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://yourclub.com"
                />
              </div>
            </div>
          )}

          {/* Private - Club only */}
          {formData.club_type === 'club' && (
            <div className="form-group form-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="is_private"
                  checked={formData.is_private}
                  onChange={handleChange}
                />
                <span>Private (invite only)</span>
              </label>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Creating...' : `Create ${formData.club_type === 'club' ? 'Club' : 'Group'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateClubModal;
